import { DestroyRef, inject, Injectable, signal } from '@angular/core'
import { debounceTime, Subject, Subscription } from 'rxjs'

import {
  ITask,
  ITaskGroup,
  ITaskLabel,
  ITaskUpdate,
  TaskAssignmentSchema,
  TaskGroupListSchema,
  TaskGroupSchema,
  TaskLabelListSchema,
  TaskLabelSchema,
  TaskSchema,
  TaskUpdateSchema,
} from '../schemas'
import { ApiService } from './api.service'

@Injectable({
  providedIn: 'root',
})
export class TasksService {
  private api = inject(ApiService)
  private routes = this.api.routes.task
  private destroyRef = inject(DestroyRef)
  private subscriptions: Subscription[] = []

  private taskUpdateSubject = new Subject<{
    id: number
    payload: Partial<ITaskUpdate>
  }>()
  private groupUpdateSubject = new Subject<{
    id: number
    payload: Partial<ITaskGroup>
  }>()

  private _groups = signal<ITaskGroup[]>([])
  private _labels = signal<ITaskLabel[]>([])

  public groups = this._groups.asReadonly()
  public labels = this._labels.asReadonly()

  constructor() {
    // Initialize data store
    const groupsSub = this.api
      .request('GET', this.routes.groups.list, TaskGroupListSchema)
      .subscribe({ next: (groups) => this._groups.set(groups) })

    const labelsSub = this.api
      .request('GET', this.routes.labels.list, TaskLabelListSchema)
      .subscribe({ next: (labels) => this._labels.set(labels) })

    // Setup debounced subject queues
    const taskUpdateSub = this.taskUpdateSubject
      .pipe(debounceTime(500))
      .subscribe((data) => {
        this.updateTask(data.id, data.payload)
      })
    const groupUpdateSub = this.groupUpdateSubject
      .pipe(debounceTime(500))
      .subscribe((data) => {
        this.updateGroup(data.id, data.payload)
      })

    // Close subscriptions when dismounted
    this.destroyRef.onDestroy(() => {
      groupsSub.unsubscribe()
      labelsSub.unsubscribe()
      taskUpdateSub.unsubscribe()
      groupUpdateSub.unsubscribe()

      for (const sub of this.subscriptions) {
        sub.unsubscribe()
      }
    })
  }

  private mapUpdateTask(
    taskId: number,
    cb: (task: ITask) => ITask,
    groupId?: number,
  ) {
    return this._groups.update((groups) =>
      groups.map((group) => {
        if (groupId != null && groupId !== group.id) {
          return group
        } else if (
          groupId == null &&
          !group.tasks.some((t) => t.id === taskId)
        ) {
          return group
        }

        group.tasks = group.tasks.map((t) => {
          if (t.id === taskId) {
            return cb(t)
          } else return t
        })

        return group
      }),
    )
  }

  createTask(payload: Partial<ITask>) {
    const $req = this.api.request('POST', this.routes.tasks.list, TaskSchema, {
      body: payload,
    })
    const sub = $req.subscribe({
      next: (task) =>
        this._groups.update((groups) =>
          groups.map((group) => {
            if (group.id === task.group) {
              group.tasks.push(task)
            }
            return group
          }),
        ),
    })

    this.subscriptions.push(sub)
    return $req
  }

  deleteTask(id: number) {
    const task = this.getTaskById(id)
    if (!task) return // Safely return if task doesn't exist

    const sub = this.api
      .request('DELETE', this.routes.tasks.detail(id))
      .subscribe({
        next: () =>
          this._groups.update((groups) =>
            groups.map((group) => {
              if (group.id === task.group) {
                group.tasks = group.tasks.filter((t) => t.id !== id)
              }
              return group
            }),
          ),
      })

    this.subscriptions.push(sub)
  }

  updateTask(id: number, payload: Partial<ITaskUpdate>) {
    const body = TaskUpdateSchema.partial().parse(payload)
    const sub = this.api
      .request('PATCH', this.routes.tasks.detail(id), TaskSchema, {
        body,
      })
      .subscribe({
        next: (resTask) =>
          this.mapUpdateTask(resTask.id, (t) => resTask, resTask.group),
      })

    this.subscriptions.push(sub)
  }

  /**
   * Update task via API.
   *
   * Debounced to allow live updating.
   */
  updateTaskDebounced(id: number, payload: Partial<ITaskUpdate>) {
    const body = TaskUpdateSchema.partial().parse(payload)
    this.taskUpdateSubject.next({ id, payload: body })
  }

  getTaskById(taskId: number) {
    for (const group of this._groups()) {
      for (const task of group.tasks) {
        if (task.id === taskId) {
          return task
        }
      }
    }

    return undefined
  }

  createGroup(payload: Partial<ITaskGroup>) {
    const req$ = this.api.request(
      'POST',
      this.routes.groups.list,
      TaskGroupSchema,
      {
        body: payload,
      },
    )

    const sub = req$.subscribe({
      next: (group) => this._groups.update((groups) => [...groups, group]),
    })

    this.subscriptions.push(sub)
    return req$
  }
  deleteGroup(id: number) {
    this._groups.set(this._groups().filter((group) => group.id !== id))
    const sub = this.api
      .request('DELETE', this.routes.groups.detail(id))
      .subscribe()
    this.subscriptions.push(sub)
  }

  updateGroup(id: number, payload: Partial<ITaskGroup>) {
    const body = TaskGroupSchema.partial().parse(payload)
    const sub = this.api
      .request('PATCH', this.routes.groups.detail(id), TaskGroupSchema, {
        body,
      })
      .subscribe({
        next: (resGroup) => {
          this._groups.update((groups) =>
            groups.map((group) => {
              if (group.id === resGroup.id) {
                return resGroup
              }
              return group
            }),
          )
        },
      })

    this.subscriptions.push(sub)
  }

  /**
   * Update task group via API.
   *
   * Debounced to allow live editing.
   */
  updateGroupDebounced(id: number, payload: Partial<ITaskGroup>) {
    const body = TaskGroupSchema.partial().parse(payload)
    this.groupUpdateSubject.next({ id, payload: body })
  }

  createLabel(payload: Partial<ITaskLabel>) {
    const sub = this.api
      .request('POST', this.routes.labels.list, TaskLabelSchema, {
        body: payload,
      })
      .subscribe({
        next: (resLabel) => {
          this._labels.update((labels) => [...labels, resLabel])
        },
      })

    this.subscriptions.push(sub)
  }

  updateLabel(id: number, payload: Partial<ITaskLabel>) {
    const sub = this.api
      .request('PATCH', this.routes.labels.detail(id), TaskLabelSchema, {
        body: payload,
      })
      .subscribe({
        next: (resLabel) => {
          this._labels.update((labels) =>
            labels.map((label) => {
              if (label.id === resLabel.id) {
                return resLabel
              } else {
                return label
              }
            }),
          )
        },
      })

    this.subscriptions.push(sub)
  }

  deleteLabel(id: number) {
    this._labels.set(this.labels().filter((label) => label.id !== id))
    const sub = this.api
      .request('DELETE', this.routes.labels.detail(id))
      .subscribe()
    this.subscriptions.push(sub)
  }

  addUserAssignment(taskId: number, userId: number) {
    const url = this.routes.tasks.assignments.list(taskId)
    const sub = this.api
      .request('POST', url, TaskAssignmentSchema, {
        body: { user: userId, task: taskId },
      })
      .subscribe({
        next: (res) =>
          this.mapUpdateTask(taskId, (t) => {
            t.user_assignments.push(res)
            return t
          }),
      })

    this.subscriptions.push(sub)
  }

  deleteUserAssignment(taskId: number, userId: number) {
    const assignment = this.getTaskById(taskId)?.user_assignments.find(
      (a) => a.user === userId,
    )
    if (!assignment) return // Safely return if doesn't exist

    const url = this.routes.tasks.assignments.detail(taskId, assignment.id)
    const sub = this.api.request('DELETE', url).subscribe({
      next: () => {
        return this.mapUpdateTask(taskId, (t) => {
          t.user_assignments = t.user_assignments.filter(
            (a) => a.id !== assignment.id,
          )
          return t
        })
      },
    })
    this.subscriptions.push(sub)
  }
}
